/**
 * NestedTabs - Two-tier tab navigation component.
 *
 * Composes two <Tabs> instances:
 *   - Primary tier: pill variant for cluster selection (skipped when only one
 *     cluster is provided — the cluster pill would be redundant in that case)
 *   - Secondary tier: line variant for section selection within the active cluster
 *
 * Features:
 *   - Cluster derivation from activeSection
 *   - OpenTelemetry trace spans (ui.admin.cluster_switch / ui.admin.section_switch)
 *     emitted from a useEffect watching activeSection, so spans reflect actual
 *     state changes rather than click intent (fixes false spans when a parent
 *     confirm() dialog cancels the switch).
 *   - scrollIntoView for active tab on deep-link
 *   - Defensive handling of empty clusters and empty sections
 */

import React, { useRef, useEffect } from 'react';
import { Tabs, type Tab } from './Tabs';
import { trace } from '@opentelemetry/api';

export interface Cluster {
  id: string;
  label: string;
  icon?: React.ReactNode;
  /**
   * Sections within this cluster. Section IDs MUST be globally unique across
   * all clusters in the `clusters` array — NestedTabs uses the first
   * cluster that contains a given section id to derive the active cluster.
   */
  sections: Tab[];
}

interface NestedTabsProps {
  clusters: Cluster[];
  activeSection: string;
  onSectionChange: (sectionId: string) => void;
  primaryAriaLabel?: string;
  secondaryAriaLabel?: string;
  /** Prefix for OTel span names. Defaults to 'ui.admin'. */
  spanPrefix?: string;
}

const tracer = trace.getTracer('clawfarm-frontend');

export const NestedTabs: React.FC<NestedTabsProps> = ({
  clusters,
  activeSection,
  onSectionChange,
  primaryAriaLabel = 'Clusters',
  secondaryAriaLabel = 'Sections',
  spanPrefix,
}) => {
  const secondaryRef = useRef<HTMLDivElement>(null);
  const prevSectionRef = useRef<string | null>(null);

  // --- Dev-mode duplicate section id detection ---
  // Runs once on mount; section IDs MUST be globally unique across clusters.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      const seen = new Map<string, string>(); // section id -> cluster id
      for (const cluster of clusters) {
        for (const section of cluster.sections) {
          const prior = seen.get(section.id);
          if (prior !== undefined) {
            console.warn(
              `[NestedTabs] Duplicate section id "${section.id}" in clusters "${prior}" and "${cluster.id}". ` +
                `NestedTabs will always pick the first cluster — section IDs MUST be globally unique.`,
            );
          } else {
            seen.set(section.id, cluster.id);
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Trace span emission on actual activeSection change ---
  // Span fires from a useEffect watching activeSection so that telemetry
  // reflects real state transitions, not click intent. If a parent's
  // confirm() dialog cancels the switch, activeSection never changes and no
  // span is emitted.
  useEffect(() => {
    // First render: store initial value, emit nothing
    if (prevSectionRef.current === null) {
      prevSectionRef.current = activeSection;
      return;
    }
    // No actual change: don't emit (handles the cancel case)
    if (prevSectionRef.current === activeSection) {
      return;
    }

    const fromSection = prevSectionRef.current;
    const toSection = activeSection;
    const fromCluster =
      clusters.find((c) => c.sections.some((s) => s.id === fromSection)) ??
      clusters[0];
    const toCluster =
      clusters.find((c) => c.sections.some((s) => s.id === toSection)) ??
      clusters[0];

    const prefix = spanPrefix ?? 'ui.admin';
    const spanName =
      toCluster.id !== fromCluster.id
        ? `${prefix}.cluster_switch`
        : `${prefix}.section_switch`;

    const span = tracer.startSpan(spanName, {
      attributes: {
        from_cluster: fromCluster.id,
        to_cluster: toCluster.id,
        from_section: fromSection,
        to_section: toSection,
      },
    });
    span.end();

    prevSectionRef.current = toSection;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  // --- scrollIntoView for active tab on deep-link ---
  // MUST run before the early return below — React hooks cannot be called
  // conditionally (rules-of-hooks).
  useEffect(() => {
    const container = secondaryRef.current;
    if (!container) return;

    const activeTab = container.querySelector(
      '[role="tab"][aria-selected="true"]',
    );
    activeTab?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activeSection]);

  // --- Defensive: empty clusters ---
  if (clusters.length === 0) {
    return null;
  }

  // --- Cluster derivation ---
  const activeCluster =
    clusters.find((c) => c.sections.some((s) => s.id === activeSection)) ??
    clusters[0];

  // --- Primary tier onChange translation ---
  // Thin translator: looks up the cluster and calls onSectionChange directly.
  // No tracing here — the useEffect above handles span emission on real state change.
  const handlePrimaryChange = (clusterId: string) => {
    const target = clusters.find((c) => c.id === clusterId);
    if (target && target.sections.length > 0) {
      onSectionChange(target.sections[0].id);
    }
    // If the cluster has no sections, do nothing (don't crash)
  };

  return (
    <div className="space-y-3">
      {/* Primary tier — pill variant. Skipped when there is only one cluster:
          a lone cluster pill has no siblings to switch between and just
          duplicates the page's own "Settings"/"Admin" header. */}
      {clusters.length > 1 && (
        <Tabs
          variant="pill"
          ariaLabel={primaryAriaLabel}
          tabs={clusters}
          activeTab={activeCluster.id}
          onChange={handlePrimaryChange}
        />
      )}

      {/* Secondary tier — line variant (only if active cluster has sections) */}
      {activeCluster.sections.length > 0 && (
        <div ref={secondaryRef}>
          <Tabs
            variant="line"
            ariaLabel={secondaryAriaLabel}
            tabs={activeCluster.sections}
            activeTab={activeSection}
            onChange={onSectionChange}
          />
        </div>
      )}
    </div>
  );
};
